import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store/store';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import {
  fetchImplementationGuide,
  patchTaskProgress,
} from '../features/iso9001/implementation/isoImplementationApi';
import { invalidateImplementationSummary } from '../features/iso9001/implementation/isoImplementationSlice';
import type {
  ImplementationGuideDetail,
  ImplementationGuidePhase,
  IsoImplementationTaskStatus,
  TaskWithProgressView,
} from '../features/iso9001/implementation/types';
import {
  ROLE_LABELS,
  STATUS_LABELS,
  isTaskDone,
  roleBadgeClass,
} from '../features/iso9001/implementation/isoImplementationUtils';
import {
  ISO_IMPLEMENTATION_META,
  type IsoImplementationStandard,
  standardGuideLabel,
} from '../features/iso9001/implementation/isoImplementationConstants';
import { IsoImplementationUnavailable } from '../features/iso9001/implementation/IsoImplementationUnavailable';
import { getApiErrorMessage } from '../features/iso9001/implementation/isoImplementationError';
import { showToast } from '../components/ui/toast.utils';

function phasePercent(phase: ImplementationGuidePhase): number {
  if (phase.tasks.length === 0) return 0;
  const done = phase.tasks.filter((t) => isTaskDone(t.progress.status)).length;
  return Math.round((done / phase.tasks.length) * 100);
}

function RoadmapOverallProgress({ phases }: { phases: ImplementationGuidePhase[] }) {
  const total = phases.reduce((sum, p) => sum + p.tasks.length, 0);
  const done = phases.reduce(
    (sum, p) => sum + p.tasks.filter((t) => isTaskDone(t.progress.status)).length,
    0,
  );
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="mt-4 space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Progreso global</span>
        <span>
          {done}/{total} tareas ({percent}%)
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-emerald-600 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function TaskRow({
  task,
  canEdit,
  onUpdated,
}: {
  task: TaskWithProgressView;
  canEdit: boolean;
  onUpdated: (task: TaskWithProgressView) => void;
}) {
  const [saving, setSaving] = useState(false);

  const toggleComplete = async () => {
    if (!canEdit || saving) return;
    setSaving(true);
    try {
      const nextStatus: IsoImplementationTaskStatus = isTaskDone(task.progress.status)
        ? 'pending'
        : 'completed';
      const updatedTask = await patchTaskProgress(task.id, { status: nextStatus });
      onUpdated(updatedTask);
      showToast(
        nextStatus === 'completed'
          ? 'Tarea marcada como completada.'
          : 'Se quitó la marca de completada.',
        'success',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-md border border-border p-3 ${isTaskDone(task.progress.status) ? 'opacity-70' : ''}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          disabled={!canEdit || saving}
          onClick={() => void toggleComplete()}
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${
            isTaskDone(task.progress.status)
              ? 'border-emerald-600 bg-emerald-600 text-white'
              : 'border-muted-foreground/40 bg-background'
          }`}
          aria-label={isTaskDone(task.progress.status) ? 'Marcar pendiente' : 'Marcar completada'}
        >
          {isTaskDone(task.progress.status) ? '✓' : ''}
        </button>
        <div className="min-w-0 flex-1">
          <p className={`font-medium ${isTaskDone(task.progress.status) ? 'line-through' : ''}`}>
            {task.title}
          </p>
          {task.detail ? (
            <p className="mt-1 text-sm text-muted-foreground">{task.detail}</p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {task.role ? (
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${roleBadgeClass(task.role)}`}>
                {ROLE_LABELS[task.role] ?? task.role}
              </span>
            ) : null}
            {task.clauseRef ? (
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                {task.clauseRef}
              </span>
            ) : null}
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {STATUS_LABELS[task.progress.status]}
            </span>
            {task.platformRoute ? (
              <Link
                to={task.platformRoute}
                className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary"
              >
                Módulo
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

interface IsoImplementationRoadmapPageProps {
  standard: IsoImplementationStandard;
  guideSlug?: string;
}

export function IsoImplementationRoadmapPage({
  standard,
  guideSlug = 'hoja-ruta',
}: IsoImplementationRoadmapPageProps) {
  const meta = ISO_IMPLEMENTATION_META[standard];
  const dispatch = useDispatch<AppDispatch>();
  const { canEdit } = usePlatformAccessLevel('iso_implementation.access');
  const [guide, setGuide] = useState<ImplementationGuideDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openPhases, setOpenPhases] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchImplementationGuide(guideSlug, standard);
      setGuide(data);
      setOpenPhases(
        Object.fromEntries(data.phases.map((phase, index) => [phase.id, index === 0])),
      );
    } catch (err) {
      setError(getApiErrorMessage(err) ?? 'No se pudo cargar la guía de implementación.');
    } finally {
      setLoading(false);
    }
  }, [guideSlug, standard]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleTaskUpdated = (updatedTask: TaskWithProgressView) => {
    setGuide((current) => {
      if (!current) return current;
      return {
        ...current,
        phases: current.phases.map((phase) => ({
          ...phase,
          tasks: phase.tasks.map((task) => (
            task.id === updatedTask.id ? updatedTask : task
          )),
        })),
      };
    });
    dispatch(invalidateImplementationSummary(standard));
  };

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Cargando guía…</p>;
  }

  if (error || !guide) {
    return (
      <IsoImplementationUnavailable
        standard={standard}
        guideSlug={guideSlug}
        title="No se pudo cargar la guía de implementación."
        detail={error}
      />
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <header className="border-b border-border pb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {meta.label} · {standardGuideLabel(standard, guideSlug)}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">{guide.title}</h1>
        {guideSlug === 'aia' && standard === 'iso14001' ? (
          <div className="mt-4 rounded-lg border border-primary/25 bg-primary/5 p-4 text-sm space-y-2">
            <p className="text-muted-foreground">
              Este anexo es un <strong className="text-foreground">catálogo de referencia</strong>{' '}
              consultoría (aspectos típicos por categoría). La matriz operativa con valoración,
              significancia y controles vive en el módulo de aspectos.
            </p>
            <Link
              to="/iso14001/aspectos"
              className="inline-flex items-center text-primary font-medium hover:underline"
            >
              Ir a Aspectos e impactos (matriz AIA operativa) →
            </Link>
            <p className="text-xs text-muted-foreground">
              <Link to={meta.progressPath} className="underline-offset-4 hover:underline">
                ← Volver al avance de implementación
              </Link>
            </p>
          </div>
        ) : null}
        {guideSlug === 'hoja-ruta' ? (
          <RoadmapOverallProgress phases={guide.phases} />
        ) : null}
      </header>

      <div className="space-y-4">
        {guide.phases.map((phase) => {
          const open = openPhases[phase.id] ?? false;
          const done = phase.tasks.filter((t) => isTaskDone(t.progress.status)).length;
          const percent = phasePercent(phase);
          return (
            <section key={phase.id} className="rounded-lg border border-border bg-card">
              <button
                type="button"
                className="flex w-full items-start justify-between gap-4 p-4 text-left"
                onClick={() =>
                  setOpenPhases((prev) => ({ ...prev, [phase.id]: !prev[phase.id] }))
                }
              >
                <div>
                  {phase.eyebrow ? (
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {phase.eyebrow}
                    </p>
                  ) : null}
                  <h2 className="text-lg font-semibold">{phase.title}</h2>
                  {phase.duration ? (
                    <p className="text-xs text-muted-foreground">{phase.duration}</p>
                  ) : null}
                </div>
                <div className="text-right text-sm text-muted-foreground min-w-[5rem]">
                  <p>
                    {done}/{phase.tasks.length}
                  </p>
                  <p className="text-xs">{percent}%</p>
                  <p className="text-lg">{open ? '▾' : '▸'}</p>
                </div>
              </button>
              <div className="px-4 pb-2">
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-emerald-600/80"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
              {open ? (
                <div className="space-y-2 border-t border-border p-4">
                  {phase.description ? (
                    <p className="text-sm italic text-muted-foreground">{phase.description}</p>
                  ) : null}
                  {phase.tasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      canEdit={canEdit}
                      onUpdated={handleTaskUpdated}
                    />
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
